import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useDebounce } from "@/hooks/useDebounce";
import { allSearchData, type SearchResult } from "@/data/searchData";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SearchBarProps {
  className?: string;
  onClose?: () => void;
}

export function SearchBar({ className, onClose }: SearchBarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Fetch roadmap items for search
  const { data: roadmapItems } = useQuery({
    queryKey: ["roadmap-items-search"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roadmap_items")
        .select("*")
        .eq("is_public", true);

      if (error) throw error;
      return data;
    },
  });

  // Enhanced search function
  const handleSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const searchTerms = query
      .toLowerCase()
      .split(" ")
      .filter((term) => term.length > 1);

    // Convert roadmap items to SearchResult format
    const roadmapSearchResults: SearchResult[] = (roadmapItems || []).map((item) => ({
      id: `roadmap-${item.id}`,
      title: item.title,
      description: item.description,
      url: `/roadmap?search=${encodeURIComponent(item.title)}`,
      category: "Roadmap" as const,
      tags: [item.category, item.status, item.priority],
    }));

    // Combine static and dynamic search data
    const combinedSearchData = [...allSearchData, ...roadmapSearchResults];

    const filteredResults = combinedSearchData
      .filter((item) => {
        const searchableText = [
          item.title,
          item.description,
          ...(item.tags || []),
          item.category,
        ]
          .join(" ")
          .toLowerCase();

        return (
          searchableText.includes(query.toLowerCase()) ||
          searchTerms.some((term) => searchableText.includes(term))
        );
      })
      .sort((a, b) => {
        // Prioritize exact title matches
        const aTitle = a.title.toLowerCase().includes(query.toLowerCase());
        const bTitle = b.title.toLowerCase().includes(query.toLowerCase());
        if (aTitle && !bTitle) return -1;
        if (!aTitle && bTitle) return 1;
        return 0;
      });

    setResults(filteredResults);
    setShowResults(true);
  }, [roadmapItems]);

  // Effect for debounced search
  useEffect(() => {
    handleSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, handleSearch]);

  // Clear search
  const handleClear = () => {
    setSearchQuery("");
    setResults([]);
    setShowResults(false);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setShowResults(false);
      onClose?.();
    }
  };

  // Handle result click
  const handleResultClick = (url: string) => {
    // Track search result click
    import("@/lib/analytics").then(({ trackSearch }) => {
      trackSearch(searchQuery, results.length);
    });
    
    // Check if URL is external
    if (url.startsWith('http')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
    setShowResults(false);
    setSearchQuery("");
    onClose?.();
  };

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    if (showResults) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showResults]);

  const categoryColors = {
    Features: "default",
    Solutions: "secondary",
    Pages: "outline",
    Blog: "default",
    API: "destructive",
    Roadmap: "default",
  } as const;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() =>
            setShowResults(searchQuery.length > 0 && results.length > 0)
          }
          className="pl-9 pr-9 w-full md:w-[300px]"
        />
        {searchQuery && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && results.length > 0 && (
        <div className="absolute top-full mt-2 w-full md:w-[400px] bg-popover border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
          <div className="p-2">
            <p className="text-xs text-muted-foreground mb-2 px-2">
              Found {results.length} result{results.length !== 1 ? "s" : ""}
            </p>
            <div className="space-y-1">
              {results.slice(0, 8).map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleResultClick(result.url)}
                  className="w-full text-left p-3 rounded-md hover:bg-accent transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <Badge variant={categoryColors[result.category]} className="text-xs">
                      {result.category}
                    </Badge>
                  </div>
                  <p className="font-medium text-sm mb-1">{result.title}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {result.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {showResults && results.length === 0 && searchQuery.trim() && (
        <div className="absolute top-full mt-2 w-full md:w-[400px] bg-popover border border-border rounded-lg shadow-lg z-50 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            No results found for "{searchQuery}"
          </p>
        </div>
      )}
    </div>
  );
}
